// NEW_FILE_JUSTIFIED: Orchestrator component for template-driven multi-step claim application wizard — distinct from PolicyApplicationWizard

import { useMemo, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, Save, Info, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { StepProgress } from '@/components/insurance/StepProgress';
import { ClaimFormSectionCard } from './ClaimFormSectionCard';
import { ReviewStep } from '@/components/insurance/policy-application/ReviewStep';
import { FieldRenderer } from '@/components/insurance/policy-application/FieldRenderer';
import { useClaimApplicationWizard } from '@/hooks/useClaimApplicationWizard';
import { FileDropZone } from './FileDropZone';
import { useClaimDocumentAnalysis } from '@/hooks/useClaimDocumentAnalysis';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: component in src/components/insurance/claim-application/ needs convex api
import { isFieldConditionallyVisible } from '@/lib/formFieldVisibility';
import type { IClaimApplicationWizardProps, IClaimPrefillDependant } from '@/@types/claims';
import type {
  IFormStep,
  IFormField,
  IRepeatableGroupEntry,
  IConditionalDetailValue,
  IReviewSectionItem,
} from '@/@types/policyFormTemplates';

const GRID_COLS_CLASS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
};

function groupFieldsBySections(fields: IFormField[]) {
  const sections: Array<{
    name: string | null;
    icon: string | null;
    columns: number;
    description: string | null;
    fields: IFormField[];
  }> = [];
  let currentSection: string | null = null;

  for (const field of fields) {
    const sectionName = field.section ?? null;
    if (sectionName !== currentSection || sections.length === 0) {
      sections.push({
        name: sectionName,
        icon: field.sectionIcon ?? null,
        columns: field.sectionColumns ?? 2,
        description: field.sectionDescription ?? null,
        fields: [field],
      });
      currentSection = sectionName;
    } else {
      sections[sections.length - 1].fields.push(field);
    }
  }

  return sections;
}

function isRequiredFieldMissing(field: IFormField, value: unknown): boolean {
  if (!field.required) return false;
  switch (field.fieldType) {
    case 'text':
    case 'email':
    case 'phone':
    case 'textarea':
    case 'select':
    case 'date':
      return !value || String(value).trim() === '';
    case 'number':
      return value === undefined || value === null || value === '';
    case 'boolean':
      return value === undefined || value === null;
    case 'checkbox':
      return value !== true;
    case 'address': {
      if (!value || typeof value !== 'object') return true;
      const a = value as Record<string, unknown>; // AS_JUSTIFIED: narrowed by object check; reading well-known address fields
      return !String(a['street'] ?? '').trim() || !String(a['city'] ?? '').trim();
    }
    case 'conditional_detail': {
      if (!value || typeof value !== 'object') return true;
      const cd = value as Record<string, unknown>; // AS_JUSTIFIED: narrowed by object check; reading 'answer' key only
      return cd['answer'] === undefined || cd['answer'] === null;
    }
    case 'repeatable_group': {
      const min = field.minEntries ?? 0;
      return min > 0 && (!Array.isArray(value) || value.length < min);
    }
    default:
      return !value;
  }
}

function countMissingRequired(step: IFormStep, values: Record<string, unknown>): number {
  let count = 0;
  for (const field of step.fields) {
    if (!isFieldConditionallyVisible(field, values)) continue;
    if (isRequiredFieldMissing(field, values[field.key])) {
      count++;
      continue;
    }
    if (field.fieldType === 'repeatable_group' && field.groupFields) {
      const entries = Array.isArray(values[field.key])
        ? (values[field.key] as IRepeatableGroupEntry[]) // AS_JUSTIFIED: narrowed by Array.isArray; repeatable_group values are IRepeatableGroupEntry[]
        : [];
      for (const entry of entries) {
        for (const sub of field.groupFields) {
          if (isRequiredFieldMissing(sub, entry.values[sub.key])) count++;
        }
      }
    }
    if (field.fieldType === 'conditional_detail' && field.subFields) {
      const cdRaw = values[field.key];
      if (cdRaw && typeof cdRaw === 'object' && 'answer' in cdRaw) {
        const cd = cdRaw as IConditionalDetailValue; // AS_JUSTIFIED: runtime narrowed by 'answer' key check
        if (cd.answer === true) {
          for (const sub of field.subFields) {
            if (isRequiredFieldMissing(sub, cd.subFieldValues?.[sub.key])) count++;
          }
        }
      }
    }
  }
  return count;
}

export function ClaimApplicationWizard({
  templateSteps,
  templateId,
  templateName: _templateName,
  orgId,
  policyId,
  existingClaimId,
  existingData,
  claimantType,
  claimantId,
  onBack,
  onComplete,
  onRegisterSaveDraft,
}: IClaimApplicationWizardProps) {
  const {
    currentStep,
    values,
    goToStep,
    nextStep,
    prevStep,
    updateValue,
    saveDraft,
    submitClaim,
    isSaving,
    isSubmitting,
  } = useClaimApplicationWizard({
    templateSteps,
    templateId,
    orgId,
    policyId,
    existingClaimId,
    existingData,
    claimantType,
    claimantId,
  });

  const { analyzeDocuments, isAnalyzing } = useClaimDocumentAnalysis(updateValue, values);

  // Register saveDraft function so parent (header button) can call it
  useEffect(() => {
    onRegisterSaveDraft?.(saveDraft);
  }, [saveDraft, onRegisterSaveDraft]);

  // ── Auto-fill from policy/member data ──────────────────────────────────────
  const prefillData = useQuery(
    api.insurance.claims.getClaimPrefillData,
    policyId && claimantType && claimantId ? { policyId, claimantType, claimantId } : 'skip'
  );

  const prefillDoneRef = useRef(false);
  useEffect(() => {
    if (prefillDoneRef.current) return;
    if (!prefillData) return;
    // Don't prefill if resuming a draft with existing data
    if (existingData && Object.keys(existingData.values).length > 0) {
      prefillDoneRef.current = true;
      return;
    }

    const { insured } = prefillData;
    const prefillMap: Record<string, unknown> = {
      insured_lastName: insured.lastName,
      insured_firstName: insured.firstName,
      insured_dateOfBirth: insured.dateOfBirth,
      insured_email: insured.email,
      insured_phone: insured.phone,
      insured_memberIdNumber: insured.memberId,
      insured_policyNumber: insured.policyNumber,
      insured_employerName: insured.employerName,
      insured_address: insured.address,
    };

    for (const [key, value] of Object.entries(prefillMap)) {
      if (value !== undefined && value !== '' && !values[key]) {
        updateValue(key, value);
      }
    }
    prefillDoneRef.current = true;
  }, [prefillData, existingData, values, updateValue]);

  // ── Patient step: person selection (self / dependant / other) ──────────────
  const patientSelection = values['_patientSelection'] as string | undefined; // AS_JUSTIFIED: values is Record<string, unknown>; _patientSelection is always string or absent

  const handlePatientSelection = (selection: string) => {
    updateValue('_patientSelection', selection);

    if (selection === 'self') {
      // Copy insured fields to patient
      updateValue('patient_sameAsInsured', true);
      const fieldMap: Record<string, string> = {
        insured_lastName: 'patient_lastName',
        insured_firstName: 'patient_firstName',
        insured_dateOfBirth: 'patient_dateOfBirth',
        insured_memberIdNumber: 'patient_memberIdNumber',
        insured_phone: 'patient_phone',
        insured_address: 'patient_address',
      };
      for (const [src, dst] of Object.entries(fieldMap)) {
        if (values[src] !== undefined) {
          updateValue(dst, values[src]);
        }
      }
      updateValue('patient_relationship', 'self');
    } else if (selection === 'other') {
      // Clear patient fields for manual entry
      updateValue('patient_sameAsInsured', false);
      updateValue('patient_lastName', '');
      updateValue('patient_firstName', '');
      updateValue('patient_dateOfBirth', '');
      updateValue('patient_memberIdNumber', '');
      updateValue('patient_phone', '');
      updateValue('patient_address', undefined);
      updateValue('patient_relationship', '');
    } else {
      // Dependant selected — find by _id
      const dep = prefillData?.dependants.find((d: IClaimPrefillDependant) => d._id === selection);
      if (dep) {
        updateValue('patient_sameAsInsured', false);
        const nameParts = dep.name.split(' ');
        const depLastName = nameParts.pop() ?? '';
        const depFirstName = nameParts.join(' ');
        updateValue('patient_lastName', depLastName);
        updateValue('patient_firstName', depFirstName);
        updateValue('patient_dateOfBirth', dep.dateOfBirth ?? '');
        updateValue('patient_memberIdNumber', dep.memberId ?? '');
        updateValue('patient_relationship', dep.relationship);
        // Clear phone/address — dependant records don't have these
        updateValue('patient_phone', '');
        updateValue('patient_address', undefined);
      }
    }
  };

  const progressSteps = useMemo(
    () => templateSteps.map(s => ({ title: s.title })),
    [templateSteps]
  );

  const missingByStep = useMemo(
    () => templateSteps.map(step => countMissingRequired(step, values)),
    [templateSteps, values]
  );

  const errorStepIndices = useMemo(
    () => missingByStep.flatMap((n, i) => (n > 0 ? [i] : [])),
    [missingByStep]
  );

  const currentTemplateStep = templateSteps[currentStep];
  const isLastStep = currentStep === templateSteps.length - 1;
  const isFirstStep = currentStep === 0;
  const isReviewStep = isLastStep && currentTemplateStep?.fields.length === 0;
  const nextStepTitle = !isLastStep ? templateSteps[currentStep + 1]?.title : null;

  const reviewSections = useMemo((): IReviewSectionItem[] => {
    if (!isReviewStep) return [];
    return templateSteps
      .filter(step => step.fields.length > 0)
      .map((step, i) => {
        const missing = missingByStep[i] ?? 0;
        const visibleFields = step.fields.filter(f => isFieldConditionallyVisible(f, values));
        const filledCount = visibleFields.filter(f => values[f.key] !== undefined).length;
        const summary =
          missing > 0
            ? `${filledCount} of ${visibleFields.length} fields completed`
            : `${visibleFields.length} fields completed`;
        return {
          key: step.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: step.title,
          summary,
          icon: 'step',
          stepIndex: i,
          hasError: missing > 0,
          errorMessage:
            missing > 0
              ? `${missing} required field${missing !== 1 ? 's' : ''} missing`
              : undefined,
        };
      });
  }, [isReviewStep, templateSteps, missingByStep, values]);

  const handleSubmit = async () => {
    await submitClaim();
    onComplete?.();
  };

  const handleBack = () => {
    if (isFirstStep) {
      onBack?.();
    } else {
      prevStep();
    }
  };

  if (!currentTemplateStep) return null;

  const sections = !isReviewStep ? groupFieldsBySections(currentTemplateStep.fields) : [];

  return (
    <div className="flex flex-col min-h-0">
      <StepProgress
        steps={progressSteps}
        currentStep={currentStep}
        onStepClick={goToStep}
        errorSteps={isReviewStep ? errorStepIndices : undefined}
        showAllLabels={true}
        fullWidth={true}
      />

      <div className="flex-1 overflow-y-auto px-8 py-6 space-y-5 bg-background-secondary">
        {/* Review step — validation summary with Edit/Fix Issues per section */}
        {isReviewStep ? (
          <ReviewStep
            sections={reviewSections}
            onEditSection={goToStep}
            onSubmit={handleSubmit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
          />
        ) : (
          <>
            {/* Instruction banner on step 1 */}
            {isFirstStep && (
              <div className="flex gap-3 rounded-xl border border-[#F59E0B40] bg-[#FFFBEB] p-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FEF3C7]">
                  <Info className="h-5 w-5 text-[#D97706]" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#92400E]" role="heading" aria-level={3}>
                    INSTRUCTIONS
                  </p>
                  <ul className="mt-1 text-[13px] text-[#92400E] leading-relaxed list-disc pl-4 space-y-1">
                    {(
                      currentTemplateStep.instructions ?? [
                        'Please upload all supporting documents (itemized bills, prescriptions, receipts) in the Provider & Treatments step.',
                        'Claims must be submitted within 120 days of the date of service to be eligible for reimbursement.',
                      ]
                    ).map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Document upload — show FIRST on Provider step or any step flagged documentUpload */}
            {(currentTemplateStep.documentUpload || currentTemplateStep.title === 'Provider') && (
              <div className="space-y-3 rounded-xl border border-border bg-card p-5">
                <div>
                  <p className="text-sm font-semibold text-foreground">Supporting Documents</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Upload itemized bills, prescriptions, receipts, and other supporting documents.
                  </p>
                </div>
                <div className="flex gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="text-xs text-blue-700">
                    Our AI will automatically analyze your documents and fill in treatment details,
                    costs, and provider information. Upload your documents first to save time.
                  </p>
                </div>
                <FileDropZone
                  files={
                    Array.isArray(values['uploaded_documents'])
                      ? (values['uploaded_documents'] as Array<{
                          storageId: string;
                          fileName: string;
                          fileSize: number;
                          mimeType: string;
                        }>) // AS_JUSTIFIED: uploaded_documents shape set by FileDropZone
                      : []
                  }
                  onFilesChange={files => updateValue('uploaded_documents', files)}
                  onNewFilesUploaded={newFiles => {
                    const ids = newFiles.map(f => f.storageId);
                    analyzeDocuments(ids);
                  }}
                />
                {isAnalyzing && (
                  <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    <p className="text-sm text-blue-700">Analyzing documents with AI...</p>
                  </div>
                )}
              </div>
            )}

            {sections.map((section, sIdx) => {
              const gridClass = GRID_COLS_CLASS[section.columns] ?? 'md:grid-cols-2';

              const fieldNodes = section.fields.map(field => {
                // Replace patient_sameAsInsured toggle with dependant dropdown
                if (field.key === 'patient_sameAsInsured' && prefillData) {
                  return (
                    <div key={field.key} className="col-span-full">
                      <Label htmlFor="patient-selection-trigger" className="text-sm font-medium">
                        This claim is for
                      </Label>
                      <Select value={patientSelection ?? ''} onValueChange={handlePatientSelection}>
                        <SelectTrigger
                          id="patient-selection-trigger"
                          data-testid="field-patient-selection"
                          className="mt-1.5 w-full"
                        >
                          <SelectValue placeholder="Select person..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="self">
                            Self ({prefillData.insured.firstName} {prefillData.insured.lastName})
                          </SelectItem>
                          {prefillData.dependants.map((dep: IClaimPrefillDependant) => (
                            <SelectItem key={dep._id} value={dep._id}>
                              {dep.name} —{' '}
                              {dep.relationship.charAt(0).toUpperCase() + dep.relationship.slice(1)}
                            </SelectItem>
                          ))}
                          <SelectItem value="other">Other (enter manually)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  );
                }

                const isFullWidth = [
                  'textarea',
                  'address',
                  'repeatable_group',
                  'conditional_detail',
                  'checkbox',
                  'info',
                ].includes(field.fieldType);
                return (
                  <div key={field.key} className={isFullWidth ? 'col-span-full' : undefined}>
                    <FieldRenderer
                      field={field}
                      value={values[field.key]}
                      onChange={updateValue}
                      allValues={values}
                    />
                  </div>
                );
              });

              if (section.name === null) {
                return (
                  <div key={sIdx} className={cn('grid gap-x-5 gap-y-4', gridClass)}>
                    {fieldNodes}
                  </div>
                );
              }

              return (
                <ClaimFormSectionCard
                  key={section.name}
                  title={section.name}
                  icon={section.icon ?? undefined}
                  description={section.description ?? undefined}
                >
                  <div className={cn('grid gap-x-5 gap-y-4', gridClass)}>{fieldNodes}</div>
                </ClaimFormSectionCard>
              );
            })}

            {/* Navigation footer */}
            <div className="flex items-center justify-between pt-3 pb-6">
              {!isFirstStep ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="gap-2 rounded-lg border-border px-6"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
              ) : (
                <div />
              )}

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={saveDraft}
                  disabled={isSaving}
                  className="gap-2 rounded-lg border-border px-6"
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? 'Saving...' : 'Save Draft'}
                </Button>

                <Button
                  type="button"
                  onClick={nextStep}
                  data-testid="claim-wizard-next"
                  className="gap-2 rounded-lg bg-[#dff8be] px-6 text-foreground hover:bg-[#d4f4a0]"
                >
                  {nextStepTitle ? `Next: ${nextStepTitle}` : 'Next'}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
